/**
 * Chutt... - Mini KPI Card
 * Compact KPI display for dashboard strips and reports
 * Trend color: >= +10% green, <= -10% red, between orange
 */
import React from 'react';
import { Box, Paper, Typography, Chip, Skeleton } from '@mui/material';
import { colors } from '../../styles/colors';
import useCountUp from '../../hooks/useCountUp';

/** Trend color: >= +10% green, <= -10% red, between orange */
function getTrendColor(change) {
  if (change >= 10) return colors.success;
  if (change <= -10) return colors.danger;
  return colors.warning;
}
function getTrendBg(change) {
  if (change >= 10) return colors.successBg;
  if (change <= -10) return colors.dangerBg;
  return colors.warningBg;
}

function MiniKPICard({ title, value, change, unit, icon, loading, color = colors.charcoal, variant = 'default', subtitle, badge, badgeIcon, badgeColor }) {
  const isHero = variant === 'hero';
  const changeColor = (change !== null && change !== undefined) ? getTrendColor(change) : colors.neutral;

  const animatedValue = useCountUp(typeof value === 'number' ? value : 0);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: isHero ? 3 : 2, borderRadius: 2, border: `1px solid ${colors.gray200}`, height: '100%' }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="40%" height={isHero ? 60 : 40} sx={{ mt: 1 }} />
        <Skeleton variant="text" width="80%" height={16} sx={{ mt: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: isHero ? 3 : 2,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        height: '100%',
        bgcolor: colors.white,
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: isHero ? 1.5 : 1, flexWrap: 'wrap' }}>
        {icon && (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { fontSize: 16, color: color } })}
          </Box>
        )}
        <Typography variant="body2" sx={{ color: colors.gray500, fontWeight: 500, fontSize: isHero ? '0.85rem' : '0.8rem' }}>
          {title}
        </Typography>
        {badge && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, position: 'absolute', top: 30, right: 8, zIndex: 5 }}>
            {badgeIcon && React.cloneElement(badgeIcon, { sx: { fontSize: 16, color: badgeColor || colors.primary } })}
            <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.6rem', color: colors.charcoal }}>
              {badge}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          fontWeight: isHero ? 800 : 'bold',
          color: colors.charcoal,
          fontSize: isHero ? '2.5rem' : '1.5rem',
          lineHeight: isHero ? 1 : undefined,
          letterSpacing: isHero ? '-0.02em' : undefined,
          fontFamily: isHero ? '"DM Sans", sans-serif' : undefined,
        }}
      >
        {typeof value === 'number' ? animatedValue.toLocaleString('fr-FR') : value}
        {unit && (
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              color: colors.gray400,
              fontSize: isHero ? '1rem' : '0.85rem',
              fontWeight: 400,
            }}
          >
            {unit}
          </Typography>
        )}
      </Typography>

      {change !== null && change !== undefined && (
        isHero ? (
          <Chip
            label={`${change > 0 ? '+' : ''}${typeof change === 'number' ? change.toFixed(1) : change}%`}
            size="small"
            sx={{
              mt: 1.5,
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 600,
              bgcolor: getTrendBg(change),
              color: changeColor,
            }}
          />
        ) : (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.3,
            mt: 0.5,
            px: 0.8,
            py: 0.2,
            borderRadius: 1,
            bgcolor: getTrendBg(change),
            animation: 'fadeIn 0.5s ease-out 0.3s both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0, transform: 'translateX(-4px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' },
            },
          }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: changeColor }}>
              {change > 0 ? '+' : ''}{typeof change === 'number' ? change.toFixed(1) : change}%
            </Typography>
          </Box>
        )
      )}

      {subtitle && (
        <Typography sx={{ color: colors.gray400, fontSize: '0.65rem', lineHeight: 1.3, mt: 0.8 }}>
          {subtitle}
        </Typography>
      )}

    </Paper>
  );
}

export default MiniKPICard;
